import React, { useContext } from "react";
import { FaEdit, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import Context from "../../../../Context/Context";

const MembersTable = ({ members, isDarkMode = true, onAction }) => {
  const { loadingUserList } = useContext(Context);

  const getInitials = (name) => {
    if (!name || typeof name !== "string") return "?";
    const parts = name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0] ? parts[0][0].toUpperCase() : "?";
  };

  const getColor = (name) => {
    if (!name) return "#FDCF08";
    const colors = [
      "#FF5733",
      "#33FF57",
      "#5733FF",
      "#FF5733",
      "#33FF57",
      "#5733FF",
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  if (loadingUserList) {
    return (
      <div className="w-full">
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div
              key={idx}
              className={`h-20 rounded-lg ${isDarkMode ? "bg-[#1a1a1a]" : "bg-gray-100"}`}></div>
          ))}
        </div>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="w-full py-12 text-center">
        <p className={`text-lg ${isDarkMode ? "text-white" : "text-gray-700"}`}>
          No members found.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Desktop Table Header - Hidden on mobile */}
      <div
        className={`hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b mb-2 ${isDarkMode ? "border-[#2a2a2a]" : "border-gray-200"}`}>
        <div className="col-span-3">
          <span
            className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-white" : "text-gray-700"}`}>
            MEMBER INFO
          </span>
        </div>
        <div className="col-span-2">
          <span
            className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-white" : "text-gray-700"}`}>
            EMAIL
          </span>
        </div>
        <div className="col-span-2">
          <span
            className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-white" : "text-gray-700"}`}>
            PHONE
          </span>
        </div>
        <div className="col-span-2">
          <span
            className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-white" : "text-gray-700"}`}>
            STATUS
          </span>
        </div>
        <div className="col-span-2">
          <span
            className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-white" : "text-gray-700"}`}>
            LAST VISIT
          </span>
        </div>
        <div className="col-span-1">
          <span
            className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-white" : "text-gray-700"}`}>
            ACTIONS
          </span>
        </div>
      </div>

      {/* Table Body */}
      <div className="space-y-2 md:space-y-2">
        {members.map((member) => {
          if (member.isArchived) return null;

          return (
            <div
              key={member.cognitoId}
              className={`md:grid md:grid-cols-12 md:gap-4 px-3 md:px-4 py-2.5 md:py-4 rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-[#1a1a1a] hover:bg-[#222222]"
                  : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
              }`}>
              {/* Mobile Card Layout */}
              <div className="md:hidden flex flex-col gap-2">
                {/* Top Row: Member Name and Avatar */}
                <div className="flex items-start gap-2">
                  {/* Member Info */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {member.imgUrl ? (
                      <img
                        src={member.imgUrl}
                        alt={member.userName}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: getColor(member.userName) }}>
                        <span
                          className={`text-xs font-semibold ${getColor(member.userName) === "#FDCF08" ? "text-black" : "text-white"}`}>
                          {getInitials(member.userName)}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <span
                        className={`font-bold text-xs leading-tight block ${isDarkMode ? "text-white" : "text-black"}`}
                        style={{
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                        }}>
                        {member.userName || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Info Rows - Compact Grid */}
                <div className="grid grid-cols-2 gap-1.5">
                  {/* Email */}
                  <div className="flex items-center gap-1.5 col-span-2">
                    <span
                      className={`text-xs truncate ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                      {member.emailId || "N/A"}
                    </span>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-1.5">
                    <FaPhone
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    <span
                      className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                      {member.phoneNumber || "N/A"}
                    </span>
                  </div>

                  {/* Country */}
                  <div className="flex items-center gap-1.5">
                    <FaMapMarkerAlt
                      className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    />
                    <span
                      className={`text-xs truncate ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                      {member.country || "N/A"}
                    </span>
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block ${
                        member.status === "Active"
                          ? "bg-green-500/20 text-green-500"
                          : "bg-red-500/20 text-red-500"
                      }`}>
                      {member.status === "Active" ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {/* Last Visit */}
                  <div className="col-span-2">
                    <span
                      className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                      {member.lastLogin
                        ? new Date(
                            parseInt(member.lastLogin),
                          ).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                </div>

                {/* Actions Row */}
                <div className="flex items-center justify-end gap-2 pt-1.5 border-t border-gray-300">
                  <button
                    onClick={() => onAction(member, "edit")}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                      isDarkMode
                        ? "hover:bg-[#2a2a2a] text-white"
                        : "hover:bg-gray-200 text-gray-700"
                    }`}
                    title="Edit">
                    <FaEdit className="text-xs" />
                    <span>Edit</span>
                  </button>
                </div>
              </div>

              {/* Desktop Grid Layout */}
              <div className="hidden md:contents">
                {/* Member Info Column - Avatar and Name */}
                <div className="col-span-3 flex items-center gap-4">
                  {/* Member Avatar and Name */}
                  <div className="flex items-center gap-3 flex-1">
                    {member.imgUrl ? (
                      <img
                        src={member.imgUrl}
                        alt={member.userName}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: getColor(member.userName) }}>
                        <span
                          className={`text-sm font-semibold ${getColor(member.userName) === "#FDCF08" ? "text-black" : "text-white"}`}>
                          {getInitials(member.userName)}
                        </span>
                      </div>
                    )}
                    <span
                      className={`font-bold text-sm ${isDarkMode ? "text-white" : "text-black"}`}>
                      {member.userName || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Email Column */}
                <div className="col-span-2 flex items-center gap-2">
                  <span
                    className={`text-sm truncate ${isDarkMode ? "text-white" : "text-gray-700"}`}>
                    {member.emailId || "N/A"}
                  </span>
                </div>

                {/* Phone Column */}
                <div className="col-span-2 flex items-center gap-2">
                  <FaPhone
                    className={`text-sm flex-shrink-0 ${isDarkMode ? "text-white" : "text-gray-700"}`}
                  />
                  <span
                    className={`text-sm ${isDarkMode ? "text-white" : "text-gray-700"}`}>
                    {member.phoneNumber || "N/A"}
                  </span>
                </div>

                {/* Status Column */}
                <div className="col-span-2 flex items-center gap-2">
                  <span
                    className={`text-xs font-semibold px-3 py-1 rounded ${
                      member.status === "Active"
                        ? "bg-green-500/20 text-green-500"
                        : "bg-red-500/20 text-red-500"
                    }`}>
                    {member.status === "Active" ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* Last Visit Column */}
                <div className="col-span-2 flex items-center gap-2">
                  <span
                    className={`text-sm ${isDarkMode ? "text-white" : "text-gray-700"}`}>
                    {member.lastLogin
                      ? new Date(
                          parseInt(member.lastLogin),
                        ).toLocaleDateString()
                      : "N/A"}
                  </span>
                </div>

                {/* Actions Column - at the end */}
                <div className="col-span-1 flex items-center justify-end gap-3">
                  <button
                    onClick={() => onAction(member, "edit")}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      isDarkMode
                        ? "hover:bg-[#2a2a2a] text-white"
                        : "hover:bg-gray-200 text-gray-700"
                    }`}
                    title="Edit">
                    <FaEdit className="text-sm" />
                    <span>Edit</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MembersTable;
